import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

interface SecurityLog {
  id: number;
  user_id?: number;
  action: string;
  resource_type: string;
  resource_id?: string;
  method?: string;
  path?: string;
  ip_address: string;
  port?: number;
  forwarded_for?: string;
  real_ip?: string;
  user_agent?: string;
  request_data?: string;
  response_data?: string;
  status_code?: number;
  duration_ms?: number;
  success: boolean;
  error_message?: string;
  session_id?: string;
  created_at: string;
  user?: {
    id: number;
    name: string;
    email: string;
  };
}

interface SecurityStats {
  total: number;
  successful: number;
  failed: number;
  successRate: number;
  actionsByType: Array<{
    action: string;
    count: string;
  }>;
  resourcesByType: Array<{
    resource_type: string;
    count: string;
  }>;
}

const SecurityDashboardPage: React.FC = () => {
  const [logs, setLogs] = useState<SecurityLog[]>([]);
  const [stats, setStats] = useState<SecurityStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({
    limit: 50,
    action: '',
    resource_type: '',
    success: '',
    ip_address: ''
  });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [logsRes, statsRes] = await Promise.all([
        api.get('/admin/audit/logs', { params: filter }),
        api.get('/admin/audit/stats')
      ]);

      // Ensure logs is always an array
      const logsData = logsRes.data?.data;
      setLogs(Array.isArray(logsData) ? logsData : []);
      setStats(statsRes.data?.data || null);
    } catch (error: any) {
      console.error('Erro ao carregar dados de segurança:', error);
      // Set empty arrays/objects on error to prevent runtime errors
      setLogs([]);
      setStats(null);
      setError(error.response?.data?.error || 'Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    loadData();
  }, [filter, loadData]);

  const getSeverityColor = (action: string, success: boolean) => {
    if (!success) return 'bg-red-100 text-red-800 border-red-200';

    switch (action.toUpperCase()) {
      case 'LOGIN':
      case 'LOGOUT':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'CREATE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'UPDATE':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'DELETE':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'SECURITY_EVENT':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getActionIcon = (action: string) => {
    switch (action.toUpperCase()) {
      case 'LOGIN':
        return '🔐';
      case 'LOGOUT':
        return '🚪';
      case 'CREATE':
        return '➕';
      case 'UPDATE':
        return '✏️';
      case 'DELETE':
        return '🗑️';
      case 'READ':
        return '👁️';
      case 'SECURITY_EVENT':
        return '🛡️';
      default:
        return '📝';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };


  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Erro ao carregar dados
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error}
              </div>
              <div className="mt-4">
                <button
                  onClick={loadData}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm transition-colors"
                >
                  Tentar novamente
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Dashboard de Segurança
        </h1>
        <p className="text-gray-600">
          Monitore atividades de segurança e logs de auditoria em tempo real
        </p>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total de Logs</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Sucessos</p>
                <p className="text-2xl font-bold text-gray-900">{stats.successful}</p>
                <p className="text-xs text-green-600">{stats.successRate}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-red-100 rounded-lg">
                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Falhas</p>
                <p className="text-2xl font-bold text-gray-900">{stats.failed}</p>
                <p className="text-xs text-red-600">{((stats.failed / stats.total) * 100).toFixed(1)}%</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Eventos de Segurança</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.actionsByType.find(a => a.action === 'SECURITY_EVENT')?.count || 0}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Filtros
          </h2>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ação
              </label>
              <select
                value={filter.action}
                onChange={(e) => setFilter(prev => ({ ...prev, action: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todas</option>
                <option value="LOGIN">Login</option>
                <option value="LOGOUT">Logout</option>
                <option value="CREATE">Criar</option>
                <option value="READ">Ler</option>
                <option value="UPDATE">Atualizar</option>
                <option value="DELETE">Deletar</option>
                <option value="SECURITY_EVENT">Evento de Segurança</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tipo de Recurso
              </label>
              <select
                value={filter.resource_type}
                onChange={(e) => setFilter(prev => ({ ...prev, resource_type: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="USER">Usuário</option>
                <option value="SERVICE">Serviço</option>
                <option value="SERVER">Servidor</option>
                <option value="PLAN">Plano</option>
                <option value="SYSTEM">Sistema</option>
                <option value="SECURITY">Segurança</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filter.success}
                onChange={(e) => setFilter(prev => ({ ...prev, success: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="true">Sucesso</option>
                <option value="false">Falha</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                IP Address
              </label>
              <input
                type="text"
                value={filter.ip_address}
                onChange={(e) => setFilter(prev => ({ ...prev, ip_address: e.target.value }))}
                placeholder="192.168.1.100"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <button
              onClick={() => setFilter({
                limit: 50,
                action: '',
                resource_type: '',
                success: '',
                ip_address: ''
              })}
              className="w-full bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Security Logs */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                Logs de Segurança ({logs.length})
              </h2>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {!Array.isArray(logs) || logs.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  Nenhum log encontrado
                </div>
              ) : (
                <div className="divide-y divide-gray-200">
                  {logs.map((log) => (
                    <div key={log.id} className="p-4 hover:bg-gray-50">
                      <div className="flex items-start space-x-3">
                        <div className="text-2xl">
                          {getActionIcon(log.action)}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getSeverityColor(log.action, log.success)}`}>
                              {log.action}
                            </span>
                            <span className="text-sm text-gray-500">
                              {log.resource_type}
                            </span>
                            {log.resource_id && (
                              <span className="text-sm text-gray-500">
                                ID: {log.resource_id}
                              </span>
                            )}
                          </div>

                          <div className="text-sm text-gray-600 mb-2">
                            <div className="flex items-center space-x-4">
                              <span>👤 {log.user?.name || 'Sistema'}</span>
                              <span>🌐 {log.ip_address}</span>
                              {log.method && log.path && (
                                <span>📡 {log.method} {log.path}</span>
                              )}
                              {log.duration_ms && (
                                <span>⚡ {log.duration_ms}ms</span>
                              )}
                            </div>
                          </div>

                          {log.error_message && (
                            <div className="text-sm text-red-600 bg-red-50 p-2 rounded mb-2">
                              ❌ {log.error_message}
                            </div>
                          )}

                          <div className="text-xs text-gray-500">
                            {formatDate(log.created_at)}
                          </div>
                        </div>

                        <div className="flex items-center space-x-2">
                          {log.success ? (
                            <div className="w-3 h-3 bg-green-500 rounded-full" title="Sucesso"></div>
                          ) : (
                            <div className="w-3 h-3 bg-red-500 rounded-full" title="Falha"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboardPage;